// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { RpcFunction, RpcCallRequest, RpcCallResponse, RpcSearchFilters } from '../../types/rpc';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import JsonEditor from '../JsonEditor';
import './RPC.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const RPC: React.FC = () => {
  const [functions, setFunctions] = useState<RpcFunction[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<RpcFunction | null>(null);
  const [callRequest, setCallRequest] = useState<RpcCallRequest>({
    id: '',
    body: '',
    user_id: '',
    http_key: ''
  });
  const [callResponse, setCallResponse] = useState<RpcCallResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(false);
  const [searchFilters, setSearchFilters] = useState<RpcSearchFilters>({
    id: '',
    runtime: ''
  });

  const sessionRole = authService.getSessionRole();

  useEffect(() => {
    loadRpcFunctions();
  }, []);

  const loadRpcFunctions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await consoleService.listRpcFunctions('');
      setFunctions(result.functions || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load RPC functions');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFunctionSelect = (func: RpcFunction) => {
    setSelectedFunction(func);
    setCallRequest(prev => ({
      ...prev,
      id: func.id,
      http_key: func.http_key || ''
    }));
    setCallResponse(null);
    setError('');
  };

  const handleCallRpc = async () => {
    if (!selectedFunction || !callRequest.id) {
      setError('Please select an RPC function to call');
      return;
    }

    setCalling(true);
    setError('');

    try {
      const response = await consoleService.callRpcFunction(callRequest);
      setCallResponse(response);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to call RPC function');
    } finally {
      setCalling(false);
    }
  };

  const handleInputChange = (field: keyof RpcCallRequest, value: string) => {
    setCallRequest(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field: keyof RpcSearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredFunctions = functions.filter(func => {
    const matchesId = !searchFilters.id || func.id.toLowerCase().includes(searchFilters.id.toLowerCase());
    const matchesRuntime = !searchFilters.runtime || func.runtime.toLowerCase().includes(searchFilters.runtime.toLowerCase());
    return matchesId && matchesRuntime;
  });

  const canCallRpc = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatJson = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  return (
    <div className="rpc-container">
      <div className="rpc-header">
        <h2 className="rpc-title">Runtime RPC Functions</h2>
        <h6 className="rpc-subtitle">{filteredFunctions.length} functions found.</h6>
      </div>

      <div className="rpc-content">
        <div className="rpc-functions-panel">
          <div className="functions-header">
            <h4>Available Functions</h4>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={loadRpcFunctions}
              disabled={loading}
            >
              {loading ? <LoadingSpinner /> : 'Refresh'}
            </button>
          </div>

          <div className="functions-filters">
            <div className="filter-group">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter by function ID..."
                value={searchFilters.id}
                onChange={(e) => handleFilterChange('id', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter by runtime..."
                value={searchFilters.runtime}
                onChange={(e) => handleFilterChange('runtime', e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              <RedTriangleIcon />
              <strong>Error loading functions:</strong> {error}
            </div>
          )}

          <div className="functions-list">
            {filteredFunctions.length === 0 && !loading && (
              <div className="no-functions">
                No RPC functions found. Make sure your runtime modules are loaded.
              </div>
            )}
            {filteredFunctions.map((func) => (
              <div
                key={func.id}
                className={`function-item ${selectedFunction?.id === func.id ? 'selected' : ''}`}
                onClick={() => handleFunctionSelect(func)}
              >
                <div className="function-id">{func.id}</div>
                <div className="function-runtime">{func.runtime}</div>
                {func.http_key && (
                  <div className="function-http-key">HTTP Key: {func.http_key}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rpc-call-panel">
          <div className="call-header">
            <h4>Call RPC Function</h4>
          </div>

          {!canCallRpc() && (
            <div className="alert alert-warning">
              You need developer permissions to call RPC functions.
            </div>
          )}

          <div className="call-form">
            <div className="form-group">
              <label htmlFor="function-id">Function ID</label>
              <input
                id="function-id"
                type="text"
                className="form-control"
                value={callRequest.id}
                onChange={(e) => handleInputChange('id', e.target.value)}
                placeholder="Enter function ID"
                disabled={!canCallRpc()}
              />
            </div>

            <div className="form-group">
              <label htmlFor="user-id">User ID (optional)</label>
              <input
                id="user-id"
                type="text"
                className="form-control"
                value={callRequest.user_id}
                onChange={(e) => handleInputChange('user_id', e.target.value)}
                placeholder="User ID to execute as"
                disabled={!canCallRpc()}
              />
            </div>

            <div className="form-group">
              <label htmlFor="http-key">HTTP Key (optional)</label>
              <input
                id="http-key"
                type="text"
                className="form-control"
                value={callRequest.http_key}
                onChange={(e) => handleInputChange('http_key', e.target.value)}
                placeholder="HTTP key for authentication"
                disabled={!canCallRpc()}
              />
            </div>

            <div className="form-group">
              <JsonEditor
                value={callRequest.body}
                onChange={(value) => handleInputChange('body', value)}
                placeholder='{"key": "value"}'
                rows={6}
                disabled={!canCallRpc()}
                label="Payload (JSON)"
              />
            </div>

            <div className="call-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCallRpc}
                disabled={!canCallRpc() || calling || !callRequest.id}
              >
                {calling ? <LoadingSpinner /> : 'Call Function'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setCallRequest({
                    id: selectedFunction?.id || '',
                    body: '',
                    user_id: '',
                    http_key: selectedFunction?.http_key || ''
                  });
                  setCallResponse(null);
                  setError('');
                }}
                disabled={!canCallRpc()}
              >
                Clear
              </button>
            </div>
          </div>

          {callResponse && (
            <div className="response-section">
              <h5>Response</h5>
              <div className="response-content">
                <div className="response-meta">
                  <span className="response-function">Function: {callResponse.id}</span>
                  {callResponse.user_id && (
                    <span className="response-user">User: {callResponse.user_id}</span>
                  )}
                </div>
                <pre className="response-payload">
                  {formatJson(callResponse.body)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RPC;
